import { z } from 'zod';
import { take } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ModalControlComponent } from "@medsurf/flat-components";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-group-annotation-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './group-annotation-form.component.html',
    styleUrls: ['./group-annotation-form.component.scss'],
    standalone: false
})
export class GroupAnnotationFormComponent {
  #isSubmitted = signal(false);
  group: FlatModels.GroupEntityModels.Group | null;
  groupAnnotationOptionArray: {value: string, name: any}[];
  groupAnnotationForm = new FormGroup(
    {
      id: new FormControl('')
    },
    {
      validators: ZodSharedValidator.validate(
        z.object({
          id: z.union([z.string().uuid().nullable(), z.literal('all')])
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
                     public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
                     public dialogService: DialogService,
                     public translate: TranslateService) {
    // Update Annotation
    this.slideAuthorFacade.currentSelectedSlideGroup$.pipe(takeUntilDestroyed()).subscribe((group) => {
      this.group = structuredClone(group);
      if (this.group) {
        this.slideAuthorFacade.groupAnnotationOptionArray$(this.group, this.translate).pipe(take(1)).subscribe((groupAnnotationOptionArray) => {
          this.groupAnnotationOptionArray = groupAnnotationOptionArray;
        });
      } else {
        this.groupAnnotationOptionArray = [];
      }
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.groupAnnotationForm.invalid || !this.groupAnnotationForm.value.id || !this.group) {
      return;
    }
    this.#isSubmitted.set(true);
    
    if (this.groupAnnotationForm.value.id === 'all') {
      const annotations = this.groupAnnotationOptionArray.reduce((arr, option) => {
        if (option.value !== 'all') {
          arr.push(option.value);
        }
        return arr;
      }, [] as string[]);
      this.slideAuthorFacade.requestAddAnnotationIdsToGroup(this.group, annotations, 'history.annotations_add_to_group').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.groupAnnotationForm);
          this.#isSubmitted.set(false);
          this.groupAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.groupAnnotationForm.reset();
        }
      });
    } else {
      // Request Add Annotation To Group
      this.slideAuthorFacade.requestAddAnnotationIdToGroup(this.group, this.groupAnnotationForm.value.id, 'history.annotation_add_to_group').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.groupAnnotationForm);
          this.#isSubmitted.set(false);
          this.groupAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.groupAnnotationForm.reset();
        }
      });
    }
  }
  
  trackByAnnotation(index: number,
                    item: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    return item.id || index;
  }
  
  goToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    this.slideAuthorFacade.requestGoToAnnotation(annotation).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  removeAnnotationFromGroup(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (!this.group) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_group_remove'),
      data: {
        label: 'global.annotation_group_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.group && remove) {
        // Request Remove Annotation From Group
        this.slideAuthorFacade.requestRemoveAnnotationFromGroup(this.group, annotation.id, 'history.annotation_remove_from_group').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
